/**
 * Client-side helpers for describing an image `File` in the UI (no upload
 * required). Used by the Branding logo tab's "review before save" state to show
 * file name, format, dimensions, and size read straight from the browser.
 */

export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Read an image File's intrinsic pixel dimensions in the browser.
 * Resolves to `null` when they can't be determined (e.g. a dimensionless SVG,
 * a decode error, or a non-DOM environment) so callers can render a fallback.
 */
export const readImageDimensions = (
  file: File,
): Promise<ImageDimensions | null> =>
  new Promise((resolve) => {
    if (
      typeof URL === "undefined" ||
      typeof URL.createObjectURL !== "function" ||
      typeof Image === "undefined"
    ) {
      resolve(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims =
        img.naturalWidth > 0 && img.naturalHeight > 0
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : null;
      URL.revokeObjectURL(objectUrl);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });

/** Human-readable file size, e.g. `"980 B"`, `"557 KB"`, `"2 MB"`. */
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
};

/** Short uppercase format label from a File's MIME type / extension, e.g. `"PNG"`, `"SVG"`. */
export const getImageFormatLabel = (file: File): string => {
  const fromMime = file.type.split("/")[1] || "";
  const raw = fromMime || file.name.split(".").pop() || "";
  const normalized = raw
    .toLowerCase()
    .replace("svg+xml", "svg")
    .replace("jpeg", "jpg");
  return normalized ? normalized.toUpperCase() : "—";
};

/** Format dimensions for display, e.g. `"4500 × 4500 px"`, or `"—"` when unknown. */
export const formatDimensions = (dims: ImageDimensions | null): string =>
  dims ? `${dims.width} × ${dims.height} px` : "—";
